import {
  Modal,
  Button,
  Input,
  Divider,
  List,
  message,
  Spin,
  Select,
} from "antd";
import {
  EnvironmentOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useRef, useState } from "react";
import { useGetCampaigns, useResolveCampaign } from "../../hooks/useCampaign";


const LocationModal = ({ open, onResolved, onClose }) => {
  const [loadingLocation, setLoadingLocation] = useState(false);

  // 🔍 search state
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debounceRef = useRef(null);

  const resolveMutation = useResolveCampaign();
  const { data: campaigns = [] } = useGetCampaigns();

  // -------------------------
  // GEO DETECT
  // -------------------------
  const handleDetect = () => {
    setLoadingLocation(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;

          const res = await resolveMutation.mutateAsync({
            lat: latitude,
            lon: longitude,
          });

          onResolved(res.data.code);
        } catch {
          message.error("Could not resolve location");
        } finally {
          setLoadingLocation(false);
        }
      },
      () => {
        message.error("Location permission denied");
        setLoadingLocation(false);
      }
    );
  };

  // -------------------------
  // NOMINATIM SEARCH
  // -------------------------
  const searchLocation = (value) => {
    setQuery(value);

    if (value.length < 3) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${value}&countrycodes=ke&limit=5`
        );
        const data = await res.json();
        setResults(data);
      } catch (err) {
        console.error(err);
        message.error("Search failed");
      }
      setSearchLoading(false);
    }, 500);
  };

  // -------------------------
  // SELECT SEARCH RESULT
  // -------------------------
  const handleSelectPlace = async (place) => {
    try {
      const lat = parseFloat(place.lat);
      const lon = parseFloat(place.lon);

      const res = await resolveMutation.mutateAsync({ lat, lon });

      onResolved(res.data.code);
    } catch {
      message.error("Could not resolve location");
    }
  };

  // -------------------------
  // MANUAL FALLBACK
  // -------------------------
  const handleManualSelect = (code) => {
    onResolved(code);
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width={480}>
      <div className="location-modal">
        <h2>Select Your Location</h2>

        {/* AUTO DETECT */}
        <Button
          icon={<EnvironmentOutlined />}
          onClick={handleDetect}
          loading={loadingLocation}
          block
          type="primary"
        >
          Use My Location
        </Button>

        {/* SEARCH */}
        <Input
          placeholder="Search your area..."
          prefix={<SearchOutlined />}
          value={query}
          onChange={(e) => searchLocation(e.target.value)}
          style={{ marginTop: 12 }}
        />

        {/* SEARCH RESULTS */}
        {searchLoading && <Spin size="small" />}

        {results.length > 0 && (
          <List
            bordered
            size="small"
            style={{ marginTop: 8, maxHeight: 200, overflowY: "auto" }}
            dataSource={results}
            renderItem={(item) => (
              <List.Item
                onClick={() => handleSelectPlace(item)}
                style={{ cursor: "pointer" }}
              >
                {item.display_name}
              </List.Item>
            )}
          />
        )}

        <Divider>or</Divider>

        {/* MANUAL FALLBACK */}
        <Select
          placeholder="Select nearest branch"
          style={{ width: "100%" }}
          options={campaigns.map((c) => ({
            label: c.name,
            value: c.code,
          }))}
          onChange={handleManualSelect}
        />
      </div>
    </Modal>
  );
};

export default LocationModal;